import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { formatNOK } from "@/lib/format";
import { SrIcon } from "@/components/ui/SrIcon";
import { OfferUnitCell } from "./OfferUnitCell";
import {
  convertOvertimeSplitUnit,
  overtimeUnitOf,
  type OvertimeSplit,
} from "@/lib/offer-calc";

interface Props {
  /** Aktiv overtidssplit. */
  split: OvertimeSplit;
  /** Fallback-enhet fra hovedlinja (t/d) når split mangler overtime_unit. */
  unit: string;
  /** Bytt felt og avgi ny split til parent. */
  onChange: (next: OvertimeSplit) => void;
  /** Lukk overtid-modus og fjern split fra linja. */
  onClear: () => void;
}

/**
 * Sub-rad som vises under en OfferLineRow når overtidstillegg er aktivt
 * (VIB-663f). Lar PL spesifisere HVOR MANGE av hovedlinjens timer som er
 * overtid, og TILLEGGSSATSEN (kr per OT-enhet). Hovedlinjens total qty og
 * normal-sats er uendret — sub-raden legger bare på et tillegg.
 * OT-enheten er uavhengig av hovedlinjen (VIB-2087).
 */
export function OfferOvertimeRow({ split, unit, onChange, onClear }: Props) {
  const overtimeUnit = overtimeUnitOf(split, unit);
  const [overtimeQty, setOvertimeQty] = useState(String(split.overtime_qty ?? 0));
  const [extraRate, setExtraRate] = useState(String(split.overtime_extra_rate ?? 0));

  useEffect(() => setOvertimeQty(String(split.overtime_qty ?? 0)), [split.overtime_qty]);
  useEffect(() => setExtraRate(String(split.overtime_extra_rate ?? 0)), [split.overtime_extra_rate]);

  function commit(field: "overtime_qty" | "overtime_extra_rate", raw: string) {
    const num = Number(raw) || 0;
    if (split[field] === num) return;
    onChange({ ...split, overtime_unit: overtimeUnit, [field]: num });
  }

  function commitUnit(nextUnit: string) {
    const toUnit = nextUnit === "d" ? "d" : "t";
    if (toUnit === overtimeUnit) return;
    onChange(convertOvertimeSplitUnit({ ...split, overtime_unit: overtimeUnit }, toUnit));
  }

  const overtimeAddon = Math.round((Number(overtimeQty) || 0) * (Number(extraRate) || 0));
  const unitLabel = overtimeUnit;

  return (
    <div
      className={cn(
        "border-b border-border/40 bg-amber-50/40 dark:bg-amber-950/10",
        "px-3 py-1.5 ml-6 mr-2 my-0.5 rounded-sm",
      )}
    >
      <div className="flex items-center gap-3 text-sm flex-wrap">
        <div className="flex items-center gap-2 shrink-0">
          <SrIcon
            name="clock"
            className="w-4 h-4 text-amber-700 dark:text-amber-400"
            aria-hidden
          />
          <span className="text-xs font-medium uppercase tracking-wider text-amber-800 dark:text-amber-400">
            Overtidstillegg
          </span>
        </div>

        <Field
          label="Overtid"
          value={overtimeQty}
          suffix={unitLabel}
          onChange={setOvertimeQty}
          onCommit={() => commit("overtime_qty", overtimeQty)}
          ariaLabel="Antall timer overtid"
        />

        <div className="w-[4.5rem] shrink-0" aria-label="Overtid enhet">
          <OfferUnitCell
            value={overtimeUnit}
            allowedUnits={["t", "d"]}
            onChange={commitUnit}
          />
        </div>

        <span className="text-muted-foreground text-xs">à</span>

        <Field
          label="Tillegg"
          value={extraRate}
          suffix={`kr/${unitLabel}`}
          onChange={setExtraRate}
          onCommit={() => commit("overtime_extra_rate", extraRate)}
          ariaLabel="Tilleggssats per time"
          wide
        />

        <div className="flex items-center gap-2 ml-auto">
          <span className="text-xs text-muted-foreground tabular-nums">
            = +{formatNOK(overtimeAddon)}
          </span>
          <button
            type="button"
            onClick={onClear}
            className="text-[11px] text-muted-foreground hover:text-destructive transition-colors"
            aria-label="Fjern overtidstillegg"
          >
            Fjern
          </button>
        </div>
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  suffix: string;
  onChange: (v: string) => void;
  onCommit: () => void;
  ariaLabel: string;
  wide?: boolean;
}

function Field({ label, value, suffix, onChange, onCommit, ariaLabel, wide }: FieldProps) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <input
        type="number"
        step="0.25"
        min={0}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onCommit}
        className={cn(
          "h-7 px-2 text-sm text-right tabular-nums bg-background rounded-sm",
          "border border-border/60 focus:border-border focus:outline-none focus:ring-1 focus:ring-amber-400/40",
          wide ? "w-24" : "w-16",
        )}
        aria-label={ariaLabel}
      />
      <span className="text-xs text-muted-foreground">{suffix}</span>
    </div>
  );
}
